import { useEditor } from '@src/stores-v2/StoreContext';
import { useVideoCreatorStore } from '@src/stores-v2/VideoCreatorStoreContext';
import {
  buildElementTimingMap,
  type ElementTiming,
  extractTimingsFromEditorElements,
  type SceneRange,
} from '@src/utility/sceneAnchors';
import React, { Fragment, useCallback, useMemo, useState } from 'react';
import styled from 'styled-components';

import { TimelineSceneBoundary } from './TimelineSceneBoundary';
import { TimelineSceneElement } from './TimelineSceneElement';

import type { Scene } from '@src/editor/models/layout/Scene';

interface TimelineScenesTrackProps {
  scenes: readonly Scene[];
  track?: number;
  isActive: boolean;
  width?: number;
  isScenesViewOnly?: boolean;
  onSplitSceneAtPlayhead?: () => boolean;
}

export const TimelineScenesTrack: React.FC<TimelineScenesTrackProps> = ({
  scenes,
  track,
  isActive,
  width,
  isScenesViewOnly = false,
  onSplitSceneAtPlayhead,
}) => {
  const videoCreator = useVideoCreatorStore();
  const editorStore = useEditor();

  const [scenePreviewRanges, setScenePreviewRanges] = useState<
    Record<string, SceneRange>
  >({});

  // Compute element timing map once for all scenes and boundaries
  // This is the key optimization: instead of each scene and boundary computing this independently,
  // we compute it once at the track level and pass it down as a prop
  const elementTimingMap = useMemo<Map<string, ElementTiming>>(() => {
    const tracksMap = editorStore.tracksMap;
    const allElements = Array.from(tracksMap.values()).flat();
    const originalElementTimings = extractTimingsFromEditorElements(
      allElements,
      el =>
        (el.type === 'video' || el.type === 'composition') &&
        videoCreator.isOriginalVideoElement(el.toJSON()),
    );
    return buildElementTimingMap(originalElementTimings);
  }, [editorStore.tracksMap, videoCreator]);

  const handleScenePreviewRanges = useCallback(
    (updates: Record<string, SceneRange | null>) => {
      setScenePreviewRanges(prev => {
        let changed = false;
        const next = { ...prev };

        Object.entries(updates).forEach(([sceneId, range]) => {
          if (!range) {
            if (sceneId in next) {
              delete next[sceneId];
              changed = true;
            }
            return;
          }

          const existing = next[sceneId];
          if (
            !existing ||
            existing.start !== range.start ||
            existing.end !== range.end
          ) {
            next[sceneId] = range;
            changed = true;
          }
        });

        return changed ? next : prev;
      });
    },
    [],
  );

  return (
    <Main
      isActive={isActive}
      style={{ width }}
      className={'timeline-track-drag-target'}
      data-track-number={track}
      data-track-type="scenes"
    >
      {scenes.map((scene, index) => {
        const nextScene = scenes[index + 1];

        return (
          <Fragment key={scene.id}>
            <TimelineSceneElement
              scene={scene}
              elementTimingMap={elementTimingMap}
              previewRanges={scenePreviewRanges}
              isViewOnly={isScenesViewOnly}
              onSplitSceneAtPlayhead={onSplitSceneAtPlayhead}
            />
            {nextScene && (
              <TimelineSceneBoundary
                leftScene={scene}
                rightScene={nextScene}
                elementTimingMap={elementTimingMap}
                previewRanges={scenePreviewRanges}
                onPreviewRanges={handleScenePreviewRanges}
                isViewOnly={isScenesViewOnly}
              />
            )}
          </Fragment>
        );
      })}
    </Main>
  );
};

const Main = styled.div<{
  isActive: boolean;
}>`
  position: relative;
  height: 30px;
  padding-top: 4px;
  background-color: ${props =>
    props.isActive
      ? 'color-mix(in srgb, var(--border) 50%, transparent)'
      : 'transparent'};
  box-sizing: border-box;
`;
